import { FC } from 'react';
import { Autocomplete, FormInputText, Svg } from '@hopskipdrive/react-components';

import { SisRecord } from './types';
import { useSisStudentSearch } from './useSisStudentSearch';

interface SisStudentSearchProps {
  onSelect: (record: SisRecord | null) => void;
  selectedStudent: SisRecord | null;
}

interface RecordOption {
  value: number;
  label: string;
  identifier: string;
  record: SisRecord;
}

const mapRecordToOption = (record: SisRecord): RecordOption => ({
  value: record.id,
  label: `${record.firstName} ${record.lastName}`,
  identifier: record.identifier,
  record,
});

export const SisStudentSearch: FC<SisStudentSearchProps> = ({ onSelect, selectedStudent }) => {
  const { records, isLoading, search, clearResults } = useSisStudentSearch();

  const options: RecordOption[] = records.map(mapRecordToOption);

  const selectedOption = selectedStudent ? mapRecordToOption(selectedStudent) : null;

  const handleInputChange = (_event: React.SyntheticEvent, value: string, reason: string) => {
    if (reason === 'input') {
      if (value.trim()) {
        search(value);
      } else {
        clearResults();
      }
    }
  };

  const handleChange = (_event: React.SyntheticEvent, option: RecordOption | null) => {
    onSelect(option?.record || null);
  };

  return (
    <Autocomplete
      id="sis-student-search"
      data-testid="sis-student-search"
      value={selectedOption}
      options={options}
      isLoading={isLoading}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onInputChange={handleInputChange}
      onChange={handleChange}
      filterOptions={(x) => x}
      noOptionsText="No students found"
      loadingText="Searching..."
      renderOption={(props, option) => (
        <li {...props} key={option.value}>
          <span style={{ fontWeight: 500 }}>{option.label}</span>
          <span style={{ marginLeft: 8, fontSize: 12, color: '#5B5F66' }}>ID: {option.identifier}</span>
        </li>
      )}
      renderInput={(params) => (
        <FormInputText
          {...params}
          variant="outlined"
          placeholder="Name or Student ID"
          InputProps={{
            ...params.InputProps,
            startAdornment: <Svg icon="search" width="20px" />,
          }}
        />
      )}
      sx={{ flex: 1 }}
    />
  );
};
